// src/utils/referenceDataSimilarityHelper.ts
// Generic duplicate/near-duplicate detection for reference data (Pet Types,
// Sizes, Breeds, Colours). Mirrors the Levenshtein approach already used by
// clientSimilarityHelper/petSimilarityHelper/vetSimilarityHelper, but is
// entity-agnostic: callers pass the already-scoped candidate list (e.g. only
// Sizes for the selected Pet Type) plus small accessor functions, rather
// than this helper knowing anything about the 4 concrete shapes.
//
// The distinction between "exact" and "near" match matters to callers:
//   - Exact (post-normalize) match -> the backend will reject this outright,
//     so callers should hard-block locally rather than round-trip an error.
//   - Near match (above threshold, below 100%) -> a human judgement call
//     ("Poodle" vs "Poodle 2"), surfaced via SimilarityDialog same as
//     Client/Pet/Vet, not blocked.

/**
 * Normalizes a reference-data name for comparison: lowercase, trimmed,
 * internal whitespace collapsed. Deliberately does not strip punctuation
 * (unlike petSimilarityHelper) since breed/colour names can legitimately
 * contain hyphens etc. that are part of the identity, not noise.
 */
const normalizeReferenceName = (name: string): string =>
  name.toLowerCase().trim().replace(/\s+/g, ' ');

const levenshteinDistance = (str1: string, str2: string): number => {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix: number[][] = [];

  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1 // deletion
        );
      }
    }
  }

  return matrix[len1][len2];
};

const calculateSimilarity = (str1: string, str2: string): number => {
  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);
  if (maxLength === 0) return 1;
  return 1 - distance / maxLength;
};

// SETTINGS:
// 0.7 (current) = moderately strict on the fallback ratio check. Safe at
// this level specifically because containment (below) independently
// catches the "X" vs "X 2" / "X Jr" pattern regardless of word length -
// without containment, short reference-data names (many Pet Types/Colours
// are 3-6 letters) would need a much looser ratio threshold to catch that
// same pattern, which in turn produces false positives between genuinely
// different short words (e.g. "Cat" vs "Bat" scores 0.667 - too close to
// a loose threshold, but nowhere near actually similar).
const SIMILARITY_THRESHOLD = 0.7;

export interface ReferenceDataSimilarityResult<T> {
  isExactMatch: boolean;
  match: T | null;
}

/**
 * Checks a candidate name against an already-scoped list of existing
 * reference-data items (e.g. Sizes already filtered to the selected Pet
 * Type). Returns the exact match if one exists (post-normalize), otherwise
 * the closest near-match, if any: either name containing the other (catches
 * "Poodle" vs "Poodle 2" reliably regardless of word length), or a
 * Levenshtein ratio at or above the threshold (catches typos like "Podle"
 * vs "Poodle"). Names under 2 characters are skipped entirely, same
 * safeguard as petSimilarityHelper, to avoid single-letter noise.
 *
 * @param newName - the name being created/renamed to
 * @param existingItems - candidates, already filtered to the relevant scope
 * @param getName - accessor for the comparable name field
 * @param getId - accessor for the item's id
 * @param excludeId - the item's own id, when editing (never flags itself)
 */
export function checkReferenceDataSimilarity<T>(
  newName: string,
  existingItems: T[],
  getName: (item: T) => string,
  getId: (item: T) => number,
  excludeId?: number
): ReferenceDataSimilarityResult<T> {
  const normalizedNew = normalizeReferenceName(newName);
  if (!normalizedNew) {
    return { isExactMatch: false, match: null };
  }

  const candidates =
    excludeId === undefined
      ? existingItems
      : existingItems.filter((item) => getId(item) !== excludeId);

  const exactMatch = candidates.find(
    (item) => normalizeReferenceName(getName(item)) === normalizedNew
  );
  if (exactMatch) {
    return { isExactMatch: true, match: exactMatch };
  }

  if (normalizedNew.length < 2) {
    return { isExactMatch: false, match: null };
  }

  let bestMatch: T | null = null;
  let bestScore = 0;
  for (const item of candidates) {
    const itemName = normalizeReferenceName(getName(item));
    if (itemName.length < 2) continue;

    const score =
      itemName.includes(normalizedNew) || normalizedNew.includes(itemName)
        ? 1
        : calculateSimilarity(normalizedNew, itemName);

    if (score >= SIMILARITY_THRESHOLD && score > bestScore) {
      bestScore = score;
      bestMatch = item;
    }
  }

  return { isExactMatch: false, match: bestMatch };
}
